/**
 * Settings View
 *
 * A unified settings page with tabbed interface for:
 * - Notifications: Browser notification preferences
 * - Delegations: Approval delegation management
 *
 * This consolidates user settings into a single page for better UX.
 */

import { apiClient } from '../services/api-client';
import { createUserSearchPicker, type DirectoryUser } from '../components/user-search-picker';
import { createDatePicker } from '../components/date-picker';
import { Modal, confirm } from '../components/modal';
import { renderIcon, createSpinner } from '../utils/icons';
import { createSidebarLayout } from '../components/sidebar-layout';
import { createNotificationSettings } from '../components/notification-settings';
import { createPaymentProfileForm } from '../components/payment-profile-form';
import type { RouteContext } from '../services/router';

// ============================================================================
// Types
// ============================================================================

type TabId = 'notifications' | 'delegations' | 'payment';

/**
 * Delegation type from backend
 */
interface Delegation {
  id: string;
  delegator_id: string;
  delegate_id: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
  created_at: string;
  revoked_at: string | null;
}

/**
 * API response types
 */
interface DelegationsResponse {
  data: {
    delegations: Delegation[];
    pagination: {
      total: number;
      offset: number;
      limit: number;
      hasMore: boolean;
    };
  };
  message: string;
}

interface DelegationResponse {
  data: Delegation;
  message: string;
}

/**
 * Delegation status for display
 */
type DelegationDisplayStatus = 'active' | 'upcoming' | 'past' | 'revoked';

/**
 * Get delegation display status based on dates
 */
function getDelegationStatus(delegation: Delegation): DelegationDisplayStatus {
  if (!delegation.is_active) {
    return 'revoked';
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const startDate = new Date(delegation.start_date);
  startDate.setHours(0, 0, 0, 0);

  const endDate = new Date(delegation.end_date);
  endDate.setHours(23, 59, 59, 999);

  if (today > endDate) {
    return 'past';
  }

  if (today >= startDate && today <= endDate) {
    return 'active';
  }

  return 'upcoming';
}

/**
 * Format date for display
 */
function formatDate(isoDate: string): string {
  const date = new Date(isoDate);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Get today's date as ISO string (YYYY-MM-DD)
 */
function getTodayISO(): string {
  const today = new Date();
  return today.toISOString().split('T')[0] ?? '';
}

// ============================================================================
// Settings View Class
// ============================================================================

export class SettingsView {
  private container: HTMLElement;
  private activeTab: TabId = 'notifications';
  private isLoading = false;
  private errorMessage: string | null = null;
  private successMessage: string | null = null;

  // Delegation data state
  private delegations: Delegation[] = [];
  private delegatesCache: Map<string, DirectoryUser> = new Map();
  private includeInactive = false;

  constructor(container: HTMLElement, initialTab?: TabId) {
    this.container = container;
    if (initialTab) {
      this.activeTab = initialTab;
    }
  }

  /**
   * Initialize and render the settings view
   */
  public async init(): Promise<void> {
    this.renderLoading();

    // Only load delegations if we're on the delegations tab
    if (this.activeTab === 'delegations') {
      await this.loadDelegations();
    }

    this.render();

    // Initialize tab-specific content after render
    if (this.activeTab === 'notifications') {
      this.initNotificationsTab();
    } else if (this.activeTab === 'payment') {
      await this.initPaymentTab();
    } else if (this.activeTab === 'delegations' && this.delegations.length === 0) {
      // Delegations already loaded above
    }
  }

  /**
   * Load delegations from API
   */
  private async loadDelegations(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = null;

    try {
      const response = await apiClient.get<DelegationsResponse>('/delegations', {
        params: {
          includeInactive: this.includeInactive,
          limit: 100,
        },
      });

      this.delegations = response.data.data.delegations;

      // Load delegate user info for each delegation
      await this.loadDelegateUsers();
    } catch (error) {
      const message = (error as { message?: string }).message || 'Failed to load delegations';
      this.errorMessage = message;
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Load user info for all delegates
   */
  private async loadDelegateUsers(): Promise<void> {
    const delegateIds = [...new Set(this.delegations.map((d) => d.delegate_id))];

    for (const id of delegateIds) {
      if (!this.delegatesCache.has(id)) {
        try {
          const response = await apiClient.get<{ data: DirectoryUser }>(`/users/${id}`);
          this.delegatesCache.set(id, response.data.data);
        } catch {
          // User may not be found, continue with others
        }
      }
    }
  }

  private renderLoading(): void {
    this.container.innerHTML = `
      <div class="min-h-screen flex items-center justify-center p-4">
        <div class="glass-card w-full max-w-md p-8 rounded-2xl text-center">
          <div class="flex justify-center mb-4">
            ${renderIcon('loader', { size: 'xl', className: 'animate-spin text-primary' }).outerHTML}
          </div>
          <p class="text-muted-foreground">Loading settings...</p>
        </div>
      </div>
    `;
  }

  private render(): void {
    this.container.innerHTML = `
      <div class="px-4 lg:px-6 py-6">
        <div class="max-w-4xl mx-auto">
          <!-- Subtitle -->
          <p class="text-muted-foreground mb-6">Manage your notification preferences, payment profile, and approval delegations</p>

          <!-- Alert Messages -->
          <div id="alert-container" class="mb-6"></div>

          <!-- Tabbed Interface -->
          <div class="glass-card rounded-2xl overflow-hidden">
            <!-- Tab Navigation -->
            <nav class="flex border-b border-border overflow-x-auto" role="tablist">
              ${this.renderTabButton('notifications', 'Notifications', 'bell')}
              ${this.renderTabButton('payment', 'Payment Profile', 'wallet')}
              ${this.renderTabButton('delegations', 'Delegations', 'userCheck')}
            </nav>

            <!-- Tab Content -->
            <div class="p-6" id="tab-content">
              ${this.renderActiveTabContent()}
            </div>
          </div>
        </div>
      </div>
    `;

    this.renderAlerts();
    this.attachEventListeners();
  }

  private renderTabButton(tabId: TabId, label: string, iconName: string): string {
    const isActive = this.activeTab === tabId;
    return `
      <button
        type="button"
        role="tab"
        aria-selected="${isActive}"
        data-tab="${tabId}"
        class="flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
          isActive
            ? 'text-primary border-primary bg-primary/5'
            : 'text-muted-foreground border-transparent hover:text-foreground hover:border-muted-foreground/50'
        }"
      >
        ${renderIcon(iconName as Parameters<typeof renderIcon>[0], { size: 'sm' }).outerHTML}
        <span>${label}</span>
      </button>
    `;
  }

  private renderActiveTabContent(): string {
    switch (this.activeTab) {
      case 'notifications':
        return this.renderNotificationsTab();
      case 'payment':
        return this.renderPaymentTab();
      case 'delegations':
        return this.renderDelegationsTab();
      default:
        return '';
    }
  }

  // ============================================================================
  // Notifications Tab
  // ============================================================================

  private renderNotificationsTab(): string {
    return `
      <div class="space-y-6">
        <div>
          <h2 class="text-lg font-semibold text-foreground mb-2">Notification Preferences</h2>
          <p class="text-sm text-muted-foreground mb-4">
            Configure how you receive notifications for expenditure request updates via email, in-app notifications, and browser push notifications.
          </p>
        </div>

        <div id="notification-preferences-container">
          <!-- Notification settings panel will be mounted here -->
        </div>
      </div>
    `;
  }

  private initNotificationsTab(): void {
    const container = this.container.querySelector('#notification-preferences-container');
    if (container instanceof HTMLElement) {
      createNotificationSettings({
        parent: container,
        onUpdate: (preferences) => {
          console.log('Notification preferences updated:', preferences);
        },
      });
    }
  }

  // ============================================================================
  // Payment Profile Tab
  // ============================================================================

  private renderPaymentTab(): string {
    return `
      <div id="payment-profile-container">
        <!-- Payment profile form will be mounted here -->
      </div>
    `;
  }

  private async initPaymentTab(): Promise<void> {
    const container = this.container.querySelector('#payment-profile-container');
    if (container instanceof HTMLElement) {
      const form = createPaymentProfileForm(container);
      try {
        await form.init();
      } catch (error) {
        console.error('Failed to initialize payment profile form:', error);
        container.innerHTML = `
          <div class="flex items-center gap-3 p-4 rounded-lg bg-destructive/20 border border-destructive/30 text-destructive" role="alert">
            ${renderIcon('alertCircle', { size: 'sm' }).outerHTML}
            <span class="flex-1">Failed to load payment profile. Please try again later.</span>
          </div>
        `;
      }
    }
  }

  // ============================================================================
  // Delegations Tab
  // ============================================================================

  private renderDelegationsTab(): string {
    if (this.isLoading) {
      return `
        <div class="flex items-center justify-center p-8">
          ${createSpinner({ size: 'lg' }).outerHTML}
          <span class="ml-3 text-muted-foreground">Loading delegations...</span>
        </div>
      `;
    }

    const activeDelegations = this.delegations.filter(
      (d) => getDelegationStatus(d) === 'active'
    );
    const upcomingDelegations = this.delegations.filter(
      (d) => getDelegationStatus(d) === 'upcoming'
    );
    const pastDelegations = this.delegations.filter((d) => {
      const status = getDelegationStatus(d);
      return status === 'past' || status === 'revoked';
    });

    return `
      <div class="space-y-6">
        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 class="text-lg font-semibold text-foreground mb-1">Approval Delegations</h2>
            <p class="text-sm text-muted-foreground">
              Assign a deputy to handle your approvals when you're away
            </p>
          </div>
          <button
            type="button"
            id="add-delegation-btn"
            class="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-accent-600 transition-colors"
          >
            ${renderIcon('plus', { size: 'sm' }).outerHTML}
            <span>Add Delegation</span>
          </button>
        </div>

        ${activeDelegations.length > 0 ? this.renderDelegationSection('Active Delegations', activeDelegations, 'active') : ''}
        ${upcomingDelegations.length > 0 ? this.renderDelegationSection('Upcoming Delegations', upcomingDelegations, 'upcoming') : ''}

        ${activeDelegations.length === 0 && upcomingDelegations.length === 0 ? this.renderEmptyDelegationsState() : ''}

        ${pastDelegations.length > 0 && this.includeInactive ? this.renderDelegationSection('Past Delegations', pastDelegations, 'past') : ''}

        ${this.renderInactiveToggle(pastDelegations.length)}
      </div>
    `;
  }

  private renderDelegationSection(
    title: string,
    delegations: Delegation[],
    type: 'active' | 'upcoming' | 'past'
  ): string {
    const badgeColor =
      type === 'active'
        ? 'bg-success/20 text-success'
        : type === 'upcoming'
          ? 'bg-info/20 text-info'
          : 'bg-muted text-muted-foreground';

    return `
      <div class="mb-6">
        <h3 class="text-md font-semibold text-foreground mb-4 flex items-center gap-2">
          ${title}
          <span class="text-xs font-normal px-2 py-0.5 rounded-full ${badgeColor}">
            ${delegations.length}
          </span>
        </h3>
        <div class="grid gap-4">
          ${delegations.map((d) => this.renderDelegationCard(d)).join('')}
        </div>
      </div>
    `;
  }

  private renderDelegationCard(delegation: Delegation): string {
    const status = getDelegationStatus(delegation);
    const delegate = this.delegatesCache.get(delegation.delegate_id);

    const statusBadgeClasses: Record<DelegationDisplayStatus, string> = {
      active: 'bg-success/20 text-success',
      upcoming: 'bg-info/20 text-info',
      past: 'bg-muted text-muted-foreground',
      revoked: 'bg-destructive/20 text-destructive',
    };

    const statusLabels: Record<DelegationDisplayStatus, string> = {
      active: 'Active',
      upcoming: 'Upcoming',
      past: 'Expired',
      revoked: 'Revoked',
    };

    const initials = this.getInitials(delegate?.displayName || 'Unknown');
    const photoHtml = delegate?.photoUrl
      ? `<img src="${delegate.photoUrl}" alt="" class="w-full h-full rounded-full object-cover">`
      : initials;

    const showActions = status === 'active' || status === 'upcoming';

    return `
      <div class="glass-card p-4 rounded-xl ${status === 'active' ? 'border-success/30' : ''} ${status === 'revoked' ? 'opacity-60' : ''}" data-delegation-id="${delegation.id}">
        <div class="flex items-start justify-between gap-4">
          <div class="flex items-center gap-4">
            <div class="w-12 h-12 rounded-full flex items-center justify-center bg-neutral-800 text-foreground shrink-0">
              ${photoHtml}
            </div>
            <div>
              <div class="flex items-center gap-2">
                <span class="font-medium text-foreground">${this.escapeHtml(delegate?.displayName || 'Unknown User')}</span>
                <span class="text-xs px-2 py-0.5 rounded-full ${statusBadgeClasses[status]}">
                  ${statusLabels[status]}
                </span>
              </div>
              <p class="text-sm text-muted-foreground">
                ${delegate?.email || delegate?.userPrincipalName || 'No email available'}
              </p>
              <div class="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                ${renderIcon('calendar', { size: 'sm' }).outerHTML}
                <span>${formatDate(delegation.start_date)} - ${formatDate(delegation.end_date)}</span>
              </div>
            </div>
          </div>
          ${showActions ? `
            <div class="flex items-center gap-2">
              <button
                type="button"
                class="edit-delegation-btn p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-neutral-800 transition-colors"
                data-delegation-id="${delegation.id}"
                title="Edit delegation"
              >
                ${renderIcon('edit', { size: 'sm' }).outerHTML}
              </button>
              <button
                type="button"
                class="delete-delegation-btn p-2 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                data-delegation-id="${delegation.id}"
                title="Revoke delegation"
              >
                ${renderIcon('trash', { size: 'sm' }).outerHTML}
              </button>
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }

  private renderEmptyDelegationsState(): string {
    return `
      <div class="glass-card p-8 rounded-xl text-center">
        <div class="flex justify-center mb-4">
          <div class="w-16 h-16 rounded-full bg-neutral-800 flex items-center justify-center">
            ${renderIcon('userCheck', { size: 'xl', className: 'text-muted-foreground' }).outerHTML}
          </div>
        </div>
        <h3 class="text-lg font-medium text-foreground mb-2">No Active Delegations</h3>
        <p class="text-muted-foreground mb-6 max-w-md mx-auto">
          You haven't set up any delegations yet. Create a delegation to assign a deputy
          who can handle your approval requests while you're away.
        </p>
        <button type="button" id="empty-add-delegation-btn" class="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-accent-600 transition-colors">
          ${renderIcon('plus', { size: 'sm' }).outerHTML}
          <span>Add Your First Delegation</span>
        </button>
      </div>
    `;
  }

  private renderInactiveToggle(pastCount: number): string {
    return `
      <div class="mt-6 pt-6 border-t border-neutral-800">
        <label class="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            id="include-inactive-toggle"
            ${this.includeInactive ? 'checked' : ''}
            class="w-5 h-5 rounded border-neutral-700 bg-neutral-900 text-primary focus:ring-primary focus:ring-offset-background"
          />
          <span class="text-sm text-muted-foreground">
            Show past and revoked delegations${pastCount > 0 ? ` (${pastCount})` : ''}
          </span>
        </label>
      </div>
    `;
  }

  // ============================================================================
  // Alert Messages
  // ============================================================================

  private renderAlerts(): void {
    const alertContainer = this.container.querySelector('#alert-container');
    if (!alertContainer) return;

    let alertHtml = '';

    if (this.errorMessage) {
      alertHtml += `
        <div class="flex items-center gap-3 p-4 rounded-lg bg-destructive/20 border border-destructive/30 text-destructive" role="alert">
          ${renderIcon('alertCircle', { size: 'sm' }).outerHTML}
          <span class="flex-1">${this.escapeHtml(this.errorMessage)}</span>
          <button type="button" class="dismiss-alert text-destructive/70 hover:text-destructive">&times;</button>
        </div>
      `;
    }

    if (this.successMessage) {
      alertHtml += `
        <div class="flex items-center gap-3 p-4 rounded-lg bg-success/20 border border-success/30 text-success" role="alert">
          ${renderIcon('checkCircle', { size: 'sm' }).outerHTML}
          <span class="flex-1">${this.escapeHtml(this.successMessage)}</span>
          <button type="button" class="dismiss-alert text-success/70 hover:text-success">&times;</button>
        </div>
      `;
    }

    alertContainer.innerHTML = alertHtml;

    // Attach dismiss listeners
    alertContainer.querySelectorAll('.dismiss-alert').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.errorMessage = null;
        this.successMessage = null;
        this.renderAlerts();
      });
    });
  }

  // ============================================================================
  // Event Listeners
  // ============================================================================

  private attachEventListeners(): void {
    // Tab navigation
    this.container.querySelectorAll('[data-tab]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const tabId = btn.getAttribute('data-tab') as TabId;
        this.switchTab(tabId);
      });
    });

    // Delegations tab specific listeners
    if (this.activeTab === 'delegations') {
      this.attachDelegationsListeners();
    }
  }

  private async switchTab(tabId: TabId): Promise<void> {
    this.activeTab = tabId;

    // Load data for the new tab if needed
    if (tabId === 'delegations' && this.delegations.length === 0) {
      this.renderLoading();
      await this.loadDelegations();
    }

    this.render();

    // Initialize tab-specific content
    if (tabId === 'notifications') {
      this.initNotificationsTab();
    } else if (tabId === 'payment') {
      await this.initPaymentTab();
    }
  }

  private attachDelegationsListeners(): void {
    // Add delegation button
    const addBtn = this.container.querySelector('#add-delegation-btn');
    addBtn?.addEventListener('click', () => this.openAddDelegationModal());

    // Empty state add button
    const emptyAddBtn = this.container.querySelector('#empty-add-delegation-btn');
    emptyAddBtn?.addEventListener('click', () => this.openAddDelegationModal());

    // Edit delegation buttons
    this.container.querySelectorAll('.edit-delegation-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const delegationId = btn.getAttribute('data-delegation-id');
        const delegation = this.delegations.find((d) => d.id === delegationId);
        if (delegation) {
          this.openEditDelegationModal(delegation);
        }
      });
    });

    // Delete delegation buttons
    this.container.querySelectorAll('.delete-delegation-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const delegationId = btn.getAttribute('data-delegation-id');
        const delegation = this.delegations.find((d) => d.id === delegationId);
        if (delegation) {
          await this.handleDeleteDelegation(delegation);
        }
      });
    });

    // Include inactive toggle
    const inactiveToggle = this.container.querySelector('#include-inactive-toggle');
    inactiveToggle?.addEventListener('change', async (e) => {
      const checkbox = e.target as HTMLInputElement;
      this.includeInactive = checkbox.checked;
      this.renderLoading();
      await this.loadDelegations();
      this.render();
    });
  }

  // ============================================================================
  // Delegation Modal Handlers
  // ============================================================================

  private openAddDelegationModal(): void {
    let selectedUser: DirectoryUser | null = null;
    let startDate = '';
    let endDate = '';

    // Create form content
    const formContent = document.createElement('div');
    formContent.className = 'space-y-6';

    // User picker
    const userField = document.createElement('div');
    userField.className = 'space-y-2';
    userField.innerHTML = `
      <label class="text-sm font-medium text-foreground">Deputy</label>
      <p class="text-xs text-muted-foreground">Select the person who will handle your approvals</p>
    `;

    const userPickerContainer = document.createElement('div');
    const userPicker = createUserSearchPicker({
      placeholder: 'Search for a user...',
      required: true,
      onSelect: (user) => {
        selectedUser = user;
      },
    });
    userPickerContainer.appendChild(userPicker);
    userField.appendChild(userPickerContainer);
    formContent.appendChild(userField);

    // Date range
    const dateRow = document.createElement('div');
    dateRow.className = 'grid grid-cols-2 gap-4';

    // Start date
    const startDateField = document.createElement('div');
    startDateField.className = 'space-y-2';
    startDateField.innerHTML = `
      <label class="text-sm font-medium text-foreground">Start Date</label>
    `;
    const startDatePicker = createDatePicker({
      required: true,
      min: getTodayISO(),
      onChange: (value) => {
        startDate = value;
        // Update end date min
        endDatePicker.min = value;
      },
    });
    startDateField.appendChild(startDatePicker);

    // End date
    const endDateField = document.createElement('div');
    endDateField.className = 'space-y-2';
    endDateField.innerHTML = `
      <label class="text-sm font-medium text-foreground">End Date</label>
    `;
    const endDatePicker = createDatePicker({
      required: true,
      min: getTodayISO(),
      onChange: (value) => {
        endDate = value;
      },
    });
    endDateField.appendChild(endDatePicker);

    dateRow.appendChild(startDateField);
    dateRow.appendChild(endDateField);
    formContent.appendChild(dateRow);

    // Error display
    const errorDisplay = document.createElement('div');
    errorDisplay.className = 'text-sm text-destructive hidden';
    formContent.appendChild(errorDisplay);

    const modal = new Modal({
      title: 'Add Delegation',
      icon: 'userPlus',
      iconClass: 'text-primary',
      content: formContent,
      size: 'md',
      actions: [
        {
          label: 'Cancel',
          variant: 'ghost',
        },
        {
          label: 'Create Delegation',
          variant: 'primary',
          primary: true,
          onClick: async () => {
            // Validate
            if (!selectedUser) {
              errorDisplay.textContent = 'Please select a deputy';
              errorDisplay.classList.remove('hidden');
              return false;
            }

            if (!startDate) {
              errorDisplay.textContent = 'Please select a start date';
              errorDisplay.classList.remove('hidden');
              return false;
            }

            if (!endDate) {
              errorDisplay.textContent = 'Please select an end date';
              errorDisplay.classList.remove('hidden');
              return false;
            }

            if (new Date(endDate) < new Date(startDate)) {
              errorDisplay.textContent = 'End date must be after start date';
              errorDisplay.classList.remove('hidden');
              return false;
            }

            try {
              await apiClient.post<DelegationResponse>('/delegations', {
                delegateId: selectedUser.id,
                startDate,
                endDate,
              });

              this.successMessage = 'Delegation created successfully';
              this.renderLoading();
              await this.loadDelegations();
              this.render();
              return true;
            } catch (error) {
              const message = (error as { message?: string }).message || 'Failed to create delegation';
              errorDisplay.textContent = message;
              errorDisplay.classList.remove('hidden');
              return false;
            }
          },
        },
      ],
    });

    modal.open();
  }

  private openEditDelegationModal(delegation: Delegation): void {
    const delegate = this.delegatesCache.get(delegation.delegate_id);
    let selectedUser: DirectoryUser | null = delegate || null;
    let startDate = delegation.start_date;
    let endDate = delegation.end_date;

    // Create form content
    const formContent = document.createElement('div');
    formContent.className = 'space-y-6';

    // User picker
    const userField = document.createElement('div');
    userField.className = 'space-y-2';
    userField.innerHTML = `
      <label class="text-sm font-medium text-foreground">Deputy</label>
      <p class="text-xs text-muted-foreground">Select the person who will handle your approvals</p>
    `;

    const userPickerContainer = document.createElement('div');
    const userPickerOptions: Parameters<typeof createUserSearchPicker>[0] = {
      placeholder: 'Search for a user...',
      required: true,
      onSelect: (user) => {
        selectedUser = user;
      },
    };
    // Only set selectedUser if delegate exists (to satisfy exactOptionalPropertyTypes)
    if (delegate) {
      userPickerOptions.selectedUser = delegate;
    }
    const userPicker = createUserSearchPicker(userPickerOptions);
    userPickerContainer.appendChild(userPicker);
    userField.appendChild(userPickerContainer);
    formContent.appendChild(userField);

    // Date range
    const dateRow = document.createElement('div');
    dateRow.className = 'grid grid-cols-2 gap-4';

    // Start date
    const startDateField = document.createElement('div');
    startDateField.className = 'space-y-2';
    startDateField.innerHTML = `
      <label class="text-sm font-medium text-foreground">Start Date</label>
    `;
    const startDatePicker = createDatePicker({
      required: true,
      value: delegation.start_date,
      min: getTodayISO(),
      onChange: (value) => {
        startDate = value;
        endDatePicker.min = value;
      },
    });
    startDateField.appendChild(startDatePicker);

    // End date
    const endDateField = document.createElement('div');
    endDateField.className = 'space-y-2';
    endDateField.innerHTML = `
      <label class="text-sm font-medium text-foreground">End Date</label>
    `;
    const endDatePicker = createDatePicker({
      required: true,
      value: delegation.end_date,
      min: delegation.start_date,
      onChange: (value) => {
        endDate = value;
      },
    });
    endDateField.appendChild(endDatePicker);

    dateRow.appendChild(startDateField);
    dateRow.appendChild(endDateField);
    formContent.appendChild(dateRow);

    // Error display
    const errorDisplay = document.createElement('div');
    errorDisplay.className = 'text-sm text-destructive hidden';
    formContent.appendChild(errorDisplay);

    const modal = new Modal({
      title: 'Edit Delegation',
      icon: 'edit',
      iconClass: 'text-primary',
      content: formContent,
      size: 'md',
      actions: [
        {
          label: 'Cancel',
          variant: 'ghost',
        },
        {
          label: 'Save Changes',
          variant: 'primary',
          primary: true,
          onClick: async () => {
            // Validate
            if (!selectedUser) {
              errorDisplay.textContent = 'Please select a deputy';
              errorDisplay.classList.remove('hidden');
              return false;
            }

            if (!startDate) {
              errorDisplay.textContent = 'Please select a start date';
              errorDisplay.classList.remove('hidden');
              return false;
            }

            if (!endDate) {
              errorDisplay.textContent = 'Please select an end date';
              errorDisplay.classList.remove('hidden');
              return false;
            }

            if (new Date(endDate) < new Date(startDate)) {
              errorDisplay.textContent = 'End date must be after start date';
              errorDisplay.classList.remove('hidden');
              return false;
            }

            try {
              await apiClient.put<DelegationResponse>(`/delegations/${delegation.id}`, {
                delegateId: selectedUser.id,
                startDate,
                endDate,
              });

              this.successMessage = 'Delegation updated successfully';
              this.renderLoading();
              await this.loadDelegations();
              this.render();
              return true;
            } catch (error) {
              const message = (error as { message?: string }).message || 'Failed to update delegation';
              errorDisplay.textContent = message;
              errorDisplay.classList.remove('hidden');
              return false;
            }
          },
        },
      ],
    });

    modal.open();
  }

  private async handleDeleteDelegation(delegation: Delegation): Promise<void> {
    const delegate = this.delegatesCache.get(delegation.delegate_id);

    const confirmed = await confirm({
      title: 'Revoke Delegation',
      message: `Are you sure you want to revoke the delegation to ${delegate?.displayName || 'this user'}? They will no longer be able to approve requests on your behalf.`,
      confirmText: 'Revoke',
      cancelText: 'Keep',
      destructive: true,
      icon: 'userMinus',
    });

    if (confirmed) {
      try {
        await apiClient.delete(`/delegations/${delegation.id}`);
        this.successMessage = 'Delegation revoked successfully';
        this.renderLoading();
        await this.loadDelegations();
        this.render();
      } catch (error) {
        const message = (error as { message?: string }).message || 'Failed to revoke delegation';
        this.errorMessage = message;
        this.render();
      }
    }
  }

  // ============================================================================
  // Utilities
  // ============================================================================

  private getInitials(name: string): string {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    const firstPart = parts[0];
    const lastPart = parts[parts.length - 1];
    if (!firstPart) return '?';
    if (parts.length === 1) return firstPart.charAt(0).toUpperCase();
    return (firstPart.charAt(0) + (lastPart?.charAt(0) ?? '')).toUpperCase();
  }

  private escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

/**
 * Create settings view factory function
 */
export function createSettingsView(container: HTMLElement, initialTab?: TabId): SettingsView {
  return new SettingsView(container, initialTab);
}

/**
 * Settings view route handler
 */
export function settingsView(context: RouteContext): HTMLElement {
  const content = document.createElement('div');

  // Determine initial tab from query parameter
  let initialTab: TabId = 'notifications';
  const tabParam = context.query.tab;
  if (tabParam === 'payment' || tabParam === 'delegations' || tabParam === 'notifications') {
    initialTab = tabParam;
  }

  const view = new SettingsView(content, initialTab);
  view.init().catch((error) => {
    console.error('Failed to initialize settings view:', error);
    content.innerHTML = `
      <div class="flex items-center justify-center p-4 min-h-[400px]">
        <div class="glass-card w-full max-w-md p-8 rounded-2xl text-center">
          <h1 class="text-xl font-bold text-foreground mb-2">Error</h1>
          <p class="text-muted-foreground mb-4">Failed to load settings. Please try again later.</p>
        </div>
      </div>
    `;
  });

  // Wrap with sidebar layout
  return createSidebarLayout(content, {
    currentPath: '/settings',
    title: 'Settings',
  });
}

export default SettingsView;
